import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { t } from 'i18next';
import { Flex } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import {
  Card,
  Checkbox,
  Colors,
  Text,
  Typography,
  TextArea,
  Button,
  TypesButton,
  DatePickerInput,
  Input,
  Icon,
  IconName,
  PopUpDropMedia,
  PopUpDropMediaType,
} from 'goalsetter-webapp-components';
import {
  Container,
  RightCard,
  RightCardWrapper,
  StyledImage,
  StyledText,
  StyledTitle,
  continueButtonStyle,
  firstCheckboxGroupStyle,
  goalCardRegisteredTrademarkStyle,
  goalsContainerStyle,
  leftCardStyle,
  mainCardsWrapperStyle,
  registeredTrademarkStyle,
  rowPaddingTop,
  secondCheckboxGroupStyle,
  textStyle,
  validFormatLabelStyle,
} from './styles';
import { ActiveGoalsEntity, FamilyMember } from 'types/goals';
import { useMediaQueryGs } from 'app/hooks/useMediaQuery';
import AddPhoto from 'assets/img/addPhoto';
import useFetchData from 'app/hooks/useFetchData';
import { AxiosError } from 'axios';
import userApi from 'api/userApi';
import GoalItem from './GoalItem';
import { CardsEntity } from 'types/goalcard';
import { sumCurrencyValues } from 'utils/string';
import { validationHandler } from './validationHandler';
import { useForm } from 'react-hook-form';
import { INPUT_NAMES } from './inputNames';
import { isEmpty } from 'lodash';
import CustomizeCardErrorModal from 'app/components/Modals/CustomizeCardErrorModal';
import LeaveFormModal from 'app/components/Modals/LeaveFormModal';
import { BASE_API } from 'utils';
import { StyledMoneyInput } from './GoalItem/styles';
import { RoutesGs } from '../../../../router/RoutesList';
import publicService from 'api/supportApi';
import { sendAnalyticsEvent } from '../../../../utils/analytics/analyticsWrapper';
import { ActionType, CategoryType } from '../../../../types/analytics/enum';
import LogoHeader from 'app/components/LogoHeader';

export const CustomizeCard: React.FC = () => {
  const { state } = useLocation();
  const { isMobile } = useMediaQueryGs();
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  const imageString = useRef<string | undefined>();
  const videoFile = useRef<File | undefined>();
  const videoUrl = useRef<string | undefined>();

  const [date, setDate] = useState<string>('');
  const [goalCardAmount, setGoalCardAmount] = useState<string>('');
  const [giveSpecificGoal, setGiveSpecificGoal] = useState<boolean>(true);
  const [showEmptyCardErrorModal, setShowEmptyCardErrorModal] =
    useState<boolean>(false);
  const handleGiveSpecificGoal = () => {
    setGiveSpecificGoal(!giveSpecificGoal);
  };
  const [now, setNow] = useState<boolean>(true);
  const { data, loading, fetchData } = useFetchData<
    ActiveGoalsEntity[],
    AxiosError
  >();
  const inputValuesInitialState = new Array(data?.length).fill({
    goalId: '',
    value: '',
  });

  const [inputValues, setInputValues] = useState<
    Array<{ goalId: string; value: string }>
  >(inputValuesInitialState);
  const [
    isShowModalUpload,
    { open: showModalUpload, close: closeModalUpload },
  ] = useDisclosure(false);

  const {
    setError,
    clearErrors,
    register,
    formState: { errors },
  } = useForm();

  const totalLimitExeeded = Boolean(
    Number(
      sumCurrencyValues([
        ...inputValues,
        { goalId: 'goalCardAmount', value: goalCardAmount },
      ]).toFixed(2),
    ) > 200,
  );

  const dateRequiredAndFilled = !now ? Boolean(date) : true;
  const requiredFieldsComplete =
    Boolean(name) && Boolean(email) && dateRequiredAndFilled;

  type GoalCard = {
    name: string;
    email: string;
    date: string;
    goalsSupport: {
      goalId: string;
      value: string;
    }[];
    childs: {
      key: string;
      value: string;
    }[];
    isSpecified: boolean;
    goalsSupportTotal: string;
    goalCardAmount: string;
    message: string;
    videoUrl: string | undefined;
    uploadedMedia: string | undefined;
  };

  const navigate = useNavigate();
  const goToSelectUser = () => navigate(RoutesGs.SendGift);
  const goToSelectCard = () => navigate(RoutesGs.SelectGoalcard);
  const goToPurchase = (goalCardData: GoalCard) =>
    navigate(RoutesGs.PurchaseGoalcard, {
      state: { ...state, customizeData: goalCardData },
    });
  const user = state?.selectedUser as FamilyMember;
  const card = state?.selectedCard as CardsEntity;
  const totalAmount = `$${
    sumCurrencyValues([
      ...inputValues,
      { goalId: 'goalCardAmount', value: goalCardAmount },
    ]).toFixed(2) || '0.00'
  }`;
  const imageUrl = `${BASE_API}/${card?.imageURL}`;

  useEffect(() => {
    setGoalCardAmount('');
    setInputValues(inputValuesInitialState);
  }, [giveSpecificGoal]);

  const handleSetNow = () => {
    setNow(!now);
  };
  const closeModal = () => setShowEmptyCardErrorModal(false);

  useEffect(() => {
    if (!user) {
      goToSelectUser();
      return;
    }
    if (!card) {
      goToSelectCard();
      return;
    }
    fetchData(userApi.getPublicGoals(state?.selectedUser.rid));

    // Scroll to Top on load.
    document.getElementById('main-container')?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const handleInputChange = (
    index: number,
    newValue: {
      goalId: string;
      value: string;
    },
  ) => {
    setInputValues((prevValues) => {
      const newValues = [...prevValues];
      newValues[index] = newValue;
      return newValues;
    });
  };

  const validateName = () =>
    validationHandler(setError, name, INPUT_NAMES.NAME);
  const validateEmail = () =>
    validationHandler(setError, email, INPUT_NAMES.EMAIL);
  const validateDate = () =>
    validationHandler(setError, date, INPUT_NAMES.DATE);

  const handleNameChange = (value: string) => {
    clearErrors(INPUT_NAMES.NAME);
    setName(value);
  };

  const handleEmailChange = (value: string) => {
    clearErrors(INPUT_NAMES.EMAIL);
    setEmail(value);
  };

  const handleDateChange = (value: string) => {
    clearErrors(INPUT_NAMES.DATE);
    setDate(value);
  };

  const uploadVideo = async (): Promise<string | undefined> => {
    if (videoFile.current) {
      const uploadForm: FormData = new FormData();
      uploadForm.append('example', videoFile.current, videoFile.current.name);
      try {
        const result = await publicService.postFile(
          videoFile.current,
          (dt: any) => console.log(dt),
        );
        return result;
      } catch (error) {
        return;
      }
    }
    return;
  };

  const convertBase64 = (file: File): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleSubmit = async () => {
    if (validateName() || validateEmail()) return;
    if (!now && validateDate()) return;
    if (Number(totalAmount?.replace('$', '')) <= 0) {
      setShowEmptyCardErrorModal(true);
      return;
    }

    if (!isEmpty(errors)) return;

    // convert to base64 animated gif
    if (videoFile.current && videoFile.current.type === 'image/gif') {
      const gifBase64 = await convertBase64(videoFile.current);

      imageString.current = gifBase64 as string;
    }

    if (videoFile.current && videoFile.current.type !== 'image/gif') {
      const videoUpload = await uploadVideo();
      videoUrl.current = videoUpload;
    }

    const goalsSupportData = inputValues.map((b) => {
      return { ...b, value: b.value.substring(1) || '0' };
    });

    const goalsChildsData = inputValues.map((b) => {
      return { key: b.goalId, value: b.value.substring(1) || '0' };
    });

    const goalCardData = {
      name: name,
      email: email,
      date: date,
      goalsSupport: goalsSupportData,
      childs: goalsChildsData,
      goalsChildsData,
      isSpecified: giveSpecificGoal,
      goalsSupportTotal:
        sumCurrencyValues([
          ...inputValues,
          { goalId: 'goalCardAmount', value: goalCardAmount },
        ]).toFixed(2) || '0.00',
      goalCardAmount: goalCardAmount.replace(/\$/g, ''),
      message: message,
      videoUrl: videoUrl.current,
      uploadedMedia: imageString.current,
      goalcardImage: imageUrl,
    };
    sendAnalyticsEvent(
      CategoryType.customizeGoalcardContinue,
      ActionType.Goalcard_CustomizeGoalcard_ContinueButton,
    );
    goToPurchase(goalCardData);
  };

  useEffect(() => {
    // Is Private user
    if (!loading && !data) {
      setGiveSpecificGoal(false);
    }
    if (data != null) {
      const goalsdata = data.map((a) => ({
        goalId: a.rid,
        value: '',
      }));
      setInputValues(goalsdata);
    }
    setGiveSpecificGoal(true);
  }, [data, loading]);

  const setImageBase64 = (image: string | File) => {
    if (typeof image === 'string') {
      videoFile.current = undefined;
      imageString.current = image;
    } else {
      imageString.current = '';
      videoFile.current = image;
    }
  };

  const RenderSelectedMedia = useCallback(() => {
    if (imageString.current) {
      return (
        <img
          src={imageString.current}
          alt="goalcard"
          height="160px"
        />
      );
    }
    if (videoFile.current && videoFile.current.type === 'image/gif') {
      return (
        <img
          src={URL.createObjectURL(videoFile.current)}
          alt="goalcard"
          height="160px"
        />
      );
    }

    if (videoFile.current && videoFile.current.type !== 'image/gif') {
      return (
        <video
          id="video"
          height="120"
          src={URL.createObjectURL(videoFile?.current)}
        />
      );
    }
    if (!imageString.current && !videoFile.current) {
      return <AddPhoto />;
    }
    return null;
  }, [videoFile, imageString]);

  const modalUploadTexts = {
    title: String(t('customizeCard.uploadPhoto.title')),
    dropYourMedia: String(t('customizeCard.uploadPhoto.dropYourMedia')),
    selectFile: String(t('customizeCard.uploadPhoto.selectFile')),
    valids: String(t('customizeCard.uploadPhoto.valids')),
    cancelButton: String(t('customizeCard.uploadPhoto.cancelButton')),
    continueButton: String(t('customizeCard.uploadPhoto.continueButton')),
    editorInstructions: String(
      t('customizeCard.uploadPhoto.editorInstructions'),
    ),
    chooseAnother: String(t('customizeCard.uploadPhoto.chooseAnother')),
    errorMedia: String(t('customizeCard.uploadPhoto.errorMedia')),
  };

  return (
    <Container>
      <LeaveFormModal backRoute={RoutesGs.SendGift} />
      <PopUpDropMedia
        onClose={closeModalUpload}
        onSubmitMedia={setImageBase64}
        show={isShowModalUpload}
        texts={modalUploadTexts}
        allowedMedia={PopUpDropMediaType.VIDEO}
      />
      <CustomizeCardErrorModal
        show={showEmptyCardErrorModal}
        onClose={closeModal}
      />
      <LogoHeader />
      <StyledTitle>
        {/* TODO: Replace this component */}
        <Trans
          i18nKey={t('selectCard.title')}
          values={{
            name: user?.firstname || 'Asd', // Check this
            reg: '®',
          }}
          components={{
            span: <span style={registeredTrademarkStyle} />,
          }}
        />
      </StyledTitle>
      <Flex
        direction={isMobile ? 'column' : 'row'}
        style={mainCardsWrapperStyle}
      >
        <Card style={leftCardStyle(isMobile)}>
          <Text
            type={Typography.GSH6}
            color={Colors.BLUE_INVESTMENTS}
          >
            {t('customizeCard.formInputs.checkBoxInputs.howToSupport.title')}
          </Text>
          {data && data.length > 0 && (
            <Flex
              direction="column"
              style={firstCheckboxGroupStyle}
            >
              <Checkbox
                label={t(
                  'customizeCard.formInputs.checkBoxInputs.howToSupport.giveSpecificGoal',
                  { name: user?.firstname },
                )}
                checked={giveSpecificGoal}
                onChange={handleGiveSpecificGoal}
              />
              <Checkbox
                label={t(
                  'customizeCard.formInputs.checkBoxInputs.howToSupport.letChoose',
                  { name: user?.firstname },
                )}
                checked={!giveSpecificGoal}
                onChange={handleGiveSpecificGoal}
              />
            </Flex>
          )}
          {giveSpecificGoal && (
            <Flex
              direction="column"
              style={goalsContainerStyle}
            >
              {data && data.length > 0 && !loading ? (
                <>
                  <Text
                    type={Typography.GSH6}
                    color={Colors.BLUE_INVESTMENTS}
                  >
                    {t('customizeCard.formInputs.whichGoalsTitle')}
                  </Text>
                  <Flex direction="column">
                    {data.map((item: ActiveGoalsEntity, index: number) => {
                      const handleGoalItemAmountChange = (newValue: string) =>
                        handleInputChange(index, {
                          goalId: item.rid,
                          value: newValue,
                        });
                      return (
                        <GoalItem
                          item={item}
                          key={item.rid}
                          value={inputValues[index]?.value}
                          onChange={handleGoalItemAmountChange}
                        />
                      );
                    })}
                  </Flex>
                  <Flex
                    justify="space-between"
                    style={rowPaddingTop}
                  >
                    <Text
                      type={Typography.GSBODY1}
                      color={totalLimitExeeded ? Colors.RED : Colors.BLACK}
                    >
                      {t('customizeCard.total')}
                    </Text>{' '}
                    <Text
                      type={Typography.GSSUBTITLE1}
                      color={totalLimitExeeded ? Colors.RED : Colors.BLACK}
                    >
                      {totalAmount}
                    </Text>
                  </Flex>
                </>
              ) : (
                <Flex
                  align="center"
                  justify="space-between"
                >
                  <Text type={Typography.GSSUBTITLE2}>
                    {t('customizeCard.enterGoalCardAmount')}
                  </Text>
                  <StyledMoneyInput
                    id="goalCardAmount"
                    placeholder="$0.00"
                    label=""
                    value={goalCardAmount}
                    onChangeValue={setGoalCardAmount}
                    maxAmount={10000}
                    minAmount={1}
                    limitexeeded={totalLimitExeeded}
                  />
                </Flex>
              )}
            </Flex>
          )}

          {!giveSpecificGoal && data && data.length > 0 && (
            <Flex
              direction="column"
              style={goalsContainerStyle}
            >
              <Text
                type={Typography.GSH6}
                color={Colors.BLUE_INVESTMENTS}
              >
                {t('customizeCard.enterAmount')}
              </Text>
              <Flex
                align="center"
                justify="space-between"
                style={{ paddingTop: '16px' }}
              >
                <Text type={Typography.GSSUBTITLE2}>
                  {t('customizeCard.goalCardLabel')}
                </Text>
                <StyledMoneyInput
                  id="goalCardAmount"
                  placeholder="$0.00"
                  label=""
                  value={goalCardAmount}
                  onChangeValue={setGoalCardAmount}
                  maxAmount={10000}
                  minAmount={0}
                  limitexeeded={totalLimitExeeded}
                />
              </Flex>
            </Flex>
          )}
          {totalLimitExeeded && (
            <Flex
              align="center"
              style={goalsContainerStyle}
            >
              {!isMobile && (
                <Icon
                  icon={IconName.ATTENTION}
                  size={30}
                  color={Colors.RED}
                />
              )}
              <Text
                type={Typography.GSBODY2}
                color={Colors.RED}
              >
                {t('customizeCard.amountLimit')}
              </Text>
            </Flex>
          )}

          <Flex
            direction="column"
            style={goalsContainerStyle}
          >
            <Text
              type={Typography.GSH6}
              color={Colors.BLUE_INVESTMENTS}
            >
              {t('customizeCard.customizeSectionTitle')}
            </Text>
            <Input
              label={String(t('customizeCard.formInputs.name.label'))}
              placeholder={t('customizeCard.formInputs.name.placeholder')}
              value={name}
              onChangeValue={handleNameChange}
              style={rowPaddingTop}
              onBlur={validateName}
              name={INPUT_NAMES.NAME}
              register={register}
              error={
                errors[INPUT_NAMES.NAME]?.message
                  ? String(errors[INPUT_NAMES.NAME]?.message)
                  : undefined
              }
            />
            <Input
              label={String(t('customizeCard.formInputs.email.label'))}
              placeholder={t('customizeCard.formInputs.email.placeholder')}
              value={email}
              onChangeValue={handleEmailChange}
              style={rowPaddingTop}
              onBlur={validateEmail}
              name={INPUT_NAMES.EMAIL}
              register={register}
              error={
                errors[INPUT_NAMES.EMAIL]?.message
                  ? String(errors[INPUT_NAMES.EMAIL]?.message)
                  : undefined
              }
            />
            <Text
              type={Typography.GSCAPTION}
              color={Colors.GRAY_DARK}
              style={rowPaddingTop}
            >
              {t('customizeCard.formInputs.checkBoxInputs.when.title')}
            </Text>
            <Flex style={secondCheckboxGroupStyle}>
              <Checkbox
                label={t(
                  'customizeCard.formInputs.checkBoxInputs.when.rightNow',
                )}
                checked={now}
                onChange={handleSetNow}
              />
              <Checkbox
                label={t(
                  'customizeCard.formInputs.checkBoxInputs.when.specificDate',
                )}
                checked={!now}
                onChange={handleSetNow}
              />
            </Flex>
            {!now && (
              <DatePickerInput
                placeholder={t('customizeCard.formInputs.date.placeholder')}
                label={t('customizeCard.formInputs.date.label')}
                onChangeValue={handleDateChange}
                value={date}
                style={rowPaddingTop}
                onBlur={validateDate}
                name={INPUT_NAMES.DATE}
                register={register}
                error={
                  errors[INPUT_NAMES.DATE]?.message
                    ? String(errors[INPUT_NAMES.DATE]?.message)
                    : undefined
                }
              />
            )}
          </Flex>
          <Flex
            direction="column"
            style={goalsContainerStyle}
          >
            <TextArea
              placeholder={t(
                'customizeCard.formInputs.specialMessage.placeholder',
              )}
              label={String(t('customizeCard.formInputs.specialMessage.label'))}
              value={message}
              onChangeValue={setMessage}
              maxLength={280}
              minRows={1}
              autosize
            />
          </Flex>
          <Flex
            align="center"
            style={secondCheckboxGroupStyle}
            onClick={showModalUpload}
          >
            <StyledText>
              {/* TODO: Replace this component */}
              <Trans
                i18nKey={t('customizeCard.addPhoto')}
                components={{
                  span: <span style={validFormatLabelStyle} />,
                }}
              />
            </StyledText>

            <RenderSelectedMedia />
          </Flex>
          {!isMobile && (
            <Button
              type={TypesButton.PRIMARY}
              onClick={handleSubmit}
              style={continueButtonStyle}
              isDisabled={
                !isEmpty(errors) || totalLimitExeeded || !requiredFieldsComplete
              }
            >
              {t('continue')}
            </Button>
          )}
        </Card>

        <RightCardWrapper isMobile={isMobile}>
          <RightCard isMobile={isMobile}>
            <Text
              type={Typography.GSH6}
              color={Colors.BLUE_INVESTMENTS}
            >
              {t('customizeCard.summarySectionTitle')}
            </Text>
            {card && (
              <StyledImage
                src={imageUrl}
                withPlaceholder
              />
            )}
            <Flex
              align="center"
              justify="space-between"
              style={rowPaddingTop}
            >
              <Text type={Typography.GSCAPTION}>
                <Trans
                  i18nKey={`<span>To</span> ${user?.firstname}`}
                  components={{
                    span: <span style={textStyle} />,
                  }}
                />
              </Text>
              <Text type={Typography.GSCAPTION}>
                <Trans
                  i18nKey={`<span>From</span> ${name || '-'}`}
                  components={{
                    span: <span style={textStyle} />,
                  }}
                />
              </Text>
            </Flex>
            <Flex
              align="baseline"
              justify="space-between"
              style={rowPaddingTop}
            >
              <Text type={Typography.GSH6}>
                {/* TODO: Replace this component */}
                <Trans
                  i18nKey={t('customizeCard.goalCard')}
                  components={{
                    span: <span style={goalCardRegisteredTrademarkStyle} />,
                  }}
                />
              </Text>
              <Text type={Typography.GSH4}>{totalAmount}</Text>
            </Flex>
          </RightCard>
        </RightCardWrapper>
      </Flex>
      {isMobile && (
        <Button
          type={TypesButton.PRIMARY}
          onClick={handleSubmit}
          style={continueButtonStyle}
          isDisabled={
            !isEmpty(errors) || totalLimitExeeded || !requiredFieldsComplete
          }
        >
          {t('continue')}
        </Button>
      )}
    </Container>
  );
};
